import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import { ref, get } from "firebase/database"; // Firebase methods
import { auth, database } from "../firebase"; // Corrected imports
import companyLogo from "../companylogo.png"; // Default logo
import { LogOut } from "lucide-react";

function UserHeader({ user }) {
  const [companyLogoUrl, setCompanyLogoUrl] = useState(companyLogo); // Default logo
  const [companyName, setCompanyName] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Get current URL path
  const menuRef = useRef(null); // Ref for mobile menu

  // Fetch user and company data
  const fetchUserData = useCallback(async (userId) => {
    try {
      const userRef = ref(database, `users/${userId}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.val();

      if (userData && userData.companyId) {
        const companyRef = ref(database, `companies/${userData.companyId}`);
        const companySnapshot = await get(companyRef);
        const companyData = companySnapshot.val();

        if (companyData) {
          setCompanyLogoUrl(companyData.logo || null);
          setCompanyName(companyData.companyName || "");
        }
      }
    } catch (error) {
      console.error("Error fetching data from Firebase:", error);
    }
  }, []);

  useEffect(() => {
    if (user?.uid) {
      fetchUserData(user.uid);
    }
  }, [user?.uid, fetchUserData]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem("userUID");
      localStorage.removeItem("userId");

      navigate("/login"); // Navigate to login page
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Function to check if the link is active
  const isActive = (path) => (location.pathname === path ? "text-black" : "text-gray-600");

  return (
    <header className="bg-white text-gray-600 py-4 border-b">
      <div className="container mx-auto flex justify-between items-center px-6">
        <Link to="/profile" className="text-lg font-bold hover:underline">
          <img src={companyLogoUrl} alt={`Company logo for ${companyName}`} className="h-10 sm:h-12 max-h-12" />
        </Link>

        {/* Hamburger button for mobile */}
        <button onClick={toggleMenu} className="block lg:hidden focus:outline-none">
          <span className="text-black text-3xl">&#9776;</span>
        </button>

        {/* Desktop Menu */}
        <nav className="hidden lg:flex space-x-4">
          {user && (
            <>
              <Link to="/profile" className={`hover:underline ${isActive("/profile")}`}>
                Profile
              </Link>
              <Link to="/emailSignature" state={{ userId: user?.uid }} className={`hover:underline ${isActive("/emailSignature")}`}>
                Email Signature
              </Link>
              <Link to="/request" className={`hover:underline ${isActive("/request")}`}>
                Request
              </Link>
              <button onClick={handleLogout} className="hover:underline flex items-center">
                Logout
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div ref={menuRef} className="lg:hidden bg-white border-2 text-black w-full absolute top-16 left-0 py-4 px-6 z-50">
          <nav className="space-y-4">
            {user && (
              <>
                <Link to="/profile" className={`hover:underline block ${isActive("/profile")}`}>
                  Profile
                </Link>
                <Link to="/emailSignature" state={{ userId: user?.uid }} className={`hover:underline block ${isActive("/emailSignature")}`}>
                  Email Signature
                </Link>
                <Link to="/request" className={`hover:underline block ${isActive("/request")}`}>
                  Support Request
                </Link>
                <button onClick={handleLogout} className="hover:underline flex items-center">
                  <LogOut className="mr-2" />
                  Logout
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

export default UserHeader;
